import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  UploadSimple,
  MagicWand,
  Sparkle,
  CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const CONFIG = {
  customers: {
    title: "Import customers & contacts",
    crumb: "Import Customers",
    sub: "Upload your customer list. AI matches your columns to the right fields — you approve before anything is saved.",
    listPath: "/customers",
    defaultMode: "add",
    modes: [
      ["add", "Add as new customers", "Every row becomes a new customer account."],
      [
        "update",
        "Update matching customers",
        "Rows matching an existing email (or name) update that customer; the rest are created.",
      ],
    ],
  },
  slabs: {
    title: "Import & update prices",
    crumb: "Import Prices",
    sub: "Upload a price list. AI reads your columns and updates each color's cost, sale and trade-group prices — you approve first.",
    listPath: "/inventory/slabs",
    defaultMode: "update",
    modes: [
      [
        "update",
        "Update prices on existing colors",
        "Rows matching an existing color update its prices; new colors are created.",
      ],
      ["add", "Add as new colors", "Every row becomes a new slab color record."],
    ],
  },
};

const TEMPLATES = {
  customers: {
    file: "customer-import-template.csv",
    headers: [
      "name", "company", "email", "phone", "secondary_phone", "address",
      "billing_address", "group", "status", "payment_terms", "notes",
      "contact_name", "contact_role", "contact_email", "contact_phone",
    ],
    sample: [
      "Alice Contracting", "Alice Contracting LLC", "alice@example.com",
      "(314) 555-0111", "(314) 555-0112", "12 Main St, St Louis MO",
      "PO Box 44, St Louis MO", "Contractor", "Active", "Net 30",
      "Prefers email", "Alice Nguyen", "Owner", "alice@example.com",
      "(314) 555-0111",
    ],
  },
  slabs: {
    file: "price-import-template.csv",
    headers: [
      "slab_color", "material", "finish", "thickness", "cost_per_sqft",
      "sale_price_per_sqft", "price_contractor", "price_designer",
      "price_builder", "price_commercial", "price_regular", "stone_group",
      "supplier", "location", "quantity", "notes",
    ],
    sample: [
      "Calacatta Gold", "Quartz", "Polished", "3cm", "18.50", "65", "52",
      "54", "53", "56", "65", "Group 5", "Stone Source", "A-12", "4",
      "Bright veining",
    ],
  },
};

const TIDY = {
  title: [
    "name", "company", "contact_name", "contact_role", "slab_color",
    "material", "finish", "supplier",
  ],
  phone: ["phone", "secondary_phone", "fax", "accounting_phone", "contact_phone"],
  email: ["email", "contact_email", "accounting_email"],
};
const titleCase = (s) =>
  s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
const normPhone = (s) => {
  const d = s.replace(/\D/g, "");
  const x = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  return x.length === 10 ? `(${x.slice(0, 3)}) ${x.slice(3, 6)}-${x.slice(6)}` : s;
};

export default function AiDataImport({ entity = "customers" }) {
  const cfg = CONFIG[entity];
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [result, setResult] = useState(null); // {headers, fields, mapping, rows, count}
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(cfg.defaultMode);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/import/analyze?entity=${entity}`, fd);
      setResult(data);
      setMapping(data.mapping || {});
      setFileName(file.name);
      toast.success(
        `${data.count} row${data.count === 1 ? "" : "s"} read — AI matched ${
          Object.values(data.mapping || {}).filter(Boolean).length
        } columns`,
      );
      if (data.matched_saved)
        toast.info("Applied your saved column layout for these columns");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that file",
      );
    }
    setBusy(false);
  };

  const downloadTemplate = () => {
    const t = TEMPLATES[entity];
    const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const csv = `${t.headers.join(",")}\n${t.sample.map(esc).join(",")}\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = t.file;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tidy = () => {
    const cleaned = (result.rows || []).map((r) => {
      const row = { ...r };
      result.fields.forEach((f) => {
        const src = mapping[f.key];
        if (!src) return;
        let v = (row[src] ?? "").toString().trim();
        if (!v) return;
        if (TIDY.title.includes(f.key)) v = titleCase(v);
        else if (TIDY.phone.includes(f.key)) v = normPhone(v);
        else if (TIDY.email.includes(f.key)) v = v.toLowerCase();
        row[src] = v;
      });
      return row;
    });
    const cols = result.fields.filter((f) => mapping[f.key]);
    const seen = new Set();
    const out = [];
    cleaned.forEach((r) => {
      const sig = cols.map((f) => (r[mapping[f.key]] || "").toLowerCase()).join("\u0001");
      if (seen.has(sig)) return;
      seen.add(sig);
      out.push(r);
    });
    const removed = cleaned.length - out.length;
    setResult({ ...result, rows: out, count: out.length });
    toast.success(
      `Tidied ${out.length} row${out.length === 1 ? "" : "s"}${
        removed ? ` · ${removed} duplicate${removed > 1 ? "s" : ""} removed` : ""
      }`,
    );
  };

  const saveLayout = async () => {
    try {
      await api.post("/import/mappings/save", {
        entity,
        headers: result.headers,
        mapping,
      });
      toast.success("Layout saved — next file with these columns maps itself");
    } catch {
      toast.error("Could not save the layout");
    }
  };

  const setField = (key, header) =>
    setMapping((m) => ({ ...m, [key]: header }));

  const missingRequired = useMemo(
    () => (result?.fields || []).filter((f) => f.required && !mapping[f.key]),
    [result, mapping],
  );

  const previewFields = useMemo(
    () => (result?.fields || []).filter((f) => mapping[f.key]).slice(0, 7),
    [result, mapping],
  );

  const confirm = async () => {
    if (missingRequired.length) {
      toast.error(`Map a column to “${missingRequired[0].label}” first`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${entity}/apply`, {
        rows: result.rows,
        mapping,
        mode,
      });
      const bits = [];
      if (data.created) bits.push(`${data.created} new`);
      if (data.updated) bits.push(`${data.updated} updated`);
      if (data.contacts) bits.push(`${data.contacts} contacts`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      toast.success(bits.join(" · ") || "Nothing to import");
      navigate(cfg.listPath);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Import failed",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={`Settings / ${cfg.crumb}`} />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/settings"
          data-testid="back-to-settings"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          {cfg.title}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">{cfg.sub}</p>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
            <UploadSimple
              size={30}
              weight="bold"
              className="mx-auto text-zinc-500"
            />
            <div className="mt-3 text-base font-semibold">
              Drop your file here
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              CSV or Excel (.xlsx) with a header row — up to 15MB
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button
                data-testid="choose-import-file"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="h-11 bg-indigo-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-500"
              >
                <MagicWand size={16} weight="bold" className="mr-2" />
                {busy ? "Reading…" : "Choose file"}
              </Button>
              <Button
                data-testid="download-import-template"
                variant="outline"
                onClick={downloadTemplate}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                Download template
              </Button>
            </div>
            <input
              ref={inputRef}
              data-testid="import-file-input"
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={pick}
            />
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
            <Sparkle
              size={14}
              weight="bold"
              className="mt-0.5 shrink-0 text-indigo-300"
            />
            Your column names can be anything — AI reads them and lines them up
            with the right fields. Check the matches below and fix any it got
            wrong before you import.
          </p>
        </div>

        {result && (
          <div className="mt-6" data-testid="import-mapping">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              <CheckCircle size={13} weight="bold" className="text-indigo-300" />
              Column matches — {fileName}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="import-tidy"
                variant="outline"
                onClick={tidy}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                <MagicWand size={14} weight="bold" className="mr-1.5" />
                Clean &amp; standardise
              </Button>
              <Button
                data-testid="save-import-mapping"
                variant="outline"
                onClick={saveLayout}
                className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
              >
                Save this layout
              </Button>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {result.fields.map((f) => (
                <label
                  key={f.key}
                  data-testid={`map-field-${f.key}`}
                  className={`flex flex-col gap-1 rounded-lg border p-3 ${
                    f.required && !mapping[f.key]
                      ? "border-red-500/40 bg-red-500/5"
                      : "border-zinc-800 bg-zinc-900"
                  }`}
                >
                  <span className="text-xs font-semibold text-zinc-300">
                    {f.label}
                    {f.required && <span className="text-red-400"> *</span>}
                  </span>
                  <select
                    data-testid={`map-select-${f.key}`}
                    value={mapping[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="h-9 rounded-md border border-zinc-700 bg-zinc-950 px-2 text-sm text-zinc-200 outline-none focus:border-indigo-500"
                  >
                    <option value="">— not imported —</option>
                    {result.headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <div
              className="mt-6 flex flex-wrap gap-2"
              data-testid="import-mode-toggle"
            >
              {cfg.modes.map(([v, label, hint]) => (
                <button
                  key={v}
                  data-testid={`import-mode-${v}`}
                  onClick={() => setMode(v)}
                  title={hint}
                  className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                    mode === v
                      ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                      : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
              <span className="self-center text-xs text-zinc-500">
                {cfg.modes.find(([v]) => v === mode)?.[2]}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-zinc-300">
                <span className="font-mono-data text-emerald-400">
                  {result.count}
                </span>{" "}
                row{result.count === 1 ? "" : "s"} ready
              </div>
              <Button
                data-testid="confirm-import-button"
                disabled={busy || result.count === 0 || missingRequired.length > 0}
                onClick={confirm}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy ? "Importing…" : `Import ${result.count} Row${result.count === 1 ? "" : "s"}`}
              </Button>
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="p-3">Row</th>
                    {previewFields.map((f) => (
                      <th key={f.key} className="p-3">
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 12).map((r, i) => (
                    <tr
                      key={i}
                      data-testid={`import-row-${i}`}
                      className="border-b border-zinc-800 last:border-0"
                    >
                      <td className="font-mono-data p-3 text-xs text-zinc-500">
                        {i + 1}
                      </td>
                      {previewFields.map((f) => (
                        <td key={f.key} className="p-3 text-zinc-300">
                          {r[mapping[f.key]] || (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.rows.length > 12 && (
                <div className="border-t border-zinc-800 p-2 text-center text-[10px] uppercase tracking-widest text-zinc-600">
                  + {result.rows.length - 12} more rows
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
